import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FileText, ShoppingCart, Receipt, Users, TrendingUp, BarChart3, Clock, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const { apiFetch, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  const fetchData = useCallback(async () => {
    const [statsRes, analyticsRes] = await Promise.all([
      apiFetch(`${API}/dashboard/stats`),
      apiFetch(`${API}/dashboard/analytics`)
    ]);
    if (statsRes.ok) setStats(await statsRes.json());
    if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    setLoading(false);
  }, [apiFetch, API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await apiFetch(`${API}/devis/send-reminders`, { method: "POST" });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message);
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setSendingReminders(false); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-200 rounded-lg" />)}</div>
          <div className="h-64 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  const maxRevenue = analytics ? Math.max(...analytics.monthly_revenue.map(m => m.revenue), 1) : 1;

  return (
    <div className="p-4 sm:p-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <Button onClick={sendReminders} disabled={sendingReminders} variant="outline" className="gap-2 text-amber-600 border-amber-200 hover:bg-amber-50" data-testid="send-reminders-btn">
          <Send className="w-4 h-4" />
          {sendingReminders ? "Envoi..." : "Relancer les devis en attente"}
        </Button>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Devis</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats?.total_devis || 0}</p>
              </div>
              <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Commandes</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats?.total_commandes || 0}</p>
              </div>
              <div className="w-10 h-10 bg-amber-50 rounded-md flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Factures</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats?.total_factures || stats?.factures || 0}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-md flex items-center justify-center"><Receipt className="w-5 h-5 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clients</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-barlow">{stats?.total_clients || 0}</p>
              </div>
              <div className="w-10 h-10 bg-slate-50 rounded-md flex items-center justify-center"><Users className="w-5 h-5 text-slate-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Section */}
      {analytics && (
        <>
          {/* Conversion & Revenue summary */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-md flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Taux de conversion</p>
                    <p className="text-2xl font-bold text-slate-900 font-barlow">{analytics.conversion_rate}%</p>
                    <p className="text-[10px] text-slate-400">Devis acceptés / total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-emerald-50/30">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Factures payées</p>
                <p className="text-2xl font-bold text-emerald-700 font-barlow">{analytics.total_paid.toLocaleString("fr-FR")} EUR</p>
                <p className="text-[10px] text-emerald-500">{analytics.factures_payees} facture(s)</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-amber-50/30">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Factures en attente</p>
                <p className="text-2xl font-bold text-amber-700 font-barlow">{analytics.total_unpaid.toLocaleString("fr-FR")} EUR</p>
                <p className="text-[10px] text-amber-500">{analytics.factures_emises} facture(s)</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-cyan-50/30">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-cyan-600 uppercase tracking-wider">Acomptes reçus</p>
                <p className="text-2xl font-bold text-cyan-700 font-barlow">{(analytics.total_acomptes || 0).toLocaleString("fr-FR")} EUR</p>
                <p className="text-[10px] text-cyan-500">{analytics.nb_acomptes || 0} acompte(s)</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Chart */}
          <Card className="border-slate-200 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Chiffre d'affaires mensuel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 h-40">
                {analytics.monthly_revenue.map((m, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[9px] text-slate-400">{m.revenue > 0 ? `${(m.revenue/1000).toFixed(1)}k` : ""}</span>
                    <div
                      className="w-full bg-[#1a3a5c] rounded-t transition-all duration-500"
                      style={{ height: `${Math.max(2, (m.revenue / maxRevenue) * 120)}px` }}
                    />
                    <span className="text-[9px] text-slate-400 truncate w-full text-center">{m.month.split(" ")[0]}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Devis Status Distribution */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Statut des devis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: "draft", label: "Brouillons", color: "bg-slate-400" },
                  { key: "sent", label: "Envoyés", color: "bg-blue-500" },
                  { key: "accepted", label: "Acceptés", color: "bg-emerald-500" },
                  { key: "rejected", label: "Refusés", color: "bg-red-400" },
                ].map(s => {
                  const count = analytics.devis_stats[s.key] || 0;
                  const total = Object.values(analytics.devis_stats).reduce((a, b) => a + b, 0) || 1;
                  return (
                    <div key={s.key} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${s.color}`} />
                      <span className="text-sm text-slate-600 w-24">{s.label}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div className={`${s.color} h-2 rounded-full transition-all`} style={{ width: `${(count / total) * 100}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-slate-900 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Top Clients */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Top Clients (CA)</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.top_clients.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_clients.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#1a3a5c] text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                          <div>
                            <p className="text-sm font-medium">{c.name || "Client"}</p>
                            <p className="text-xs text-slate-400">{c.count} facture(s)</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{c.total.toLocaleString("fr-FR")} EUR</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">Aucune donnée</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Interventions */}
          {analytics.upcoming_interventions?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Prochaines Interventions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics.upcoming_interventions.map(i => (
                    <div key={i.intervention_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                      <div>
                        <p className="text-sm font-medium">{i.title}</p>
                        <p className="text-xs text-slate-400">{i.location} {i.client_name ? `- ${i.client_name}` : ""}</p>
                      </div>
                      <span className="text-xs text-slate-500">{new Date(i.date_debut).toLocaleDateString("fr-FR")}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
